import { List } from '@mui/material';
import React, { useMemo, useState, useEffect } from 'react'
import { getVariantText } from '../../../../../../utils/helper';
import { useSelector } from 'react-redux';
import RenderNodeValues from './RenderNodeValues';

const RenderProperty = ({properties, onListUpdate}) => {
    const [propertyList, setPropertyList] = useState(properties);
    const scope = useSelector((state) => state.container.scope);
    const openAsset = useSelector((state) => state.openAsset);

    // Helper function to check if property matches any of the given keywords
    const propertyMatchesKeywords = (property, keywords) => {
      return keywords.some(keyword =>
        property.nodeName?.toUpperCase().includes(keyword.toUpperCase()) ||
        property.labelTranslation?.some(label =>
          label.title?.toUpperCase().includes(keyword.toUpperCase())
        )
      );
    };

    // Define 11 common fields (show for ALL asset types)
    const commonFields = [
      'LOCATION', 'ADRESSE', 'STANDORT', 'LAGE', 'ORT',
      'CONSTRUCTION YEAR', 'BAUJAHR', 'ERRICHTUNGSJAHR',
      'TYPE OF USE', 'NUTZUNGSART', 'NUTZUNG', 'VERWENDUNG',
      'PLOT AREA', 'GRUNDSTÜCKSFLÄCHE', 'GRUNDFLÄCHE',
      'GROSS FLOOR AREA', 'BRUTTOGRUNDFLÄCHE', 'BGF',
      'RENTAL AREA', 'MIETFLÄCHE',
      'TOTAL FLOORS', 'STOCKWERKE INSG', 'GESAMTSTOCKWERKE',
      'FLOORS ABOVE GROUND', 'STOCKWERKE OBERIRDISCH', 'OBERIRDISCHE STOCKWERKE',
      'FLOORS UNDERGROUND', 'STOCKWERKE UNTERIRDISCH', 'UNTERIRDISCHE STOCKWERKE',
      'TOTAL CAR PARKING SPACES', 'GESAMTE ANZAHL PARKPLÄTZE', 'PARKPLÄTZE GESAMT',
      'PARKING SPACES WITH E-CHARGING STATION', 'STELLPLÄTZE MIT E-LADESTATION', 'E-LADESTATION'
    ];

    // Define special fields (show only for specific asset types)
    const specialFieldsByType = {
      'Wohnen': [
        'NUMBER OF FLATS', 'ANZAHL DER WOHNUNGEN', 'ANZAHL WOHNUNGEN', 'WOHNUNGSANZAHL'
      ],
      'Logistik': [
        'FLOOR LOAD CAPACITY', 'TRAGFÄHIGKEIT BODEN', 'BODENBELASTUNG',
        'CLEAR HEIGHT', 'LICHTE HÖHE', 'DECKENHÖHE', 'RAUMHÖHE'
      ],
      'Hotel': [
        'NUMBER OF GUESTROOMS', 'ANZAHL GÄSTEZIMMER', 'ZIMMERANZAHL', 'GÄSTERÄUME'
      ]
    };

    // Filter properties to show only common fields + special fields for current asset type
    useEffect(() => {
      // Map English asset type names to German (for special fields lookup)
      const assetTypeMapping = {
        'Logistic': 'Logistik',
        'Logistics': 'Logistik',
        'Residential': 'Wohnen',
        'Hotel': 'Hotel'
      };

      // Collect ALL sub-asset types (for multi-sub-asset support in RedFlag/FullReport)
      const subAssetTypes = [];

      // Try getting from subAssetList (RedFlag/FullReport may have multiple sub-assets)
      if (openAsset?.subAssetList && openAsset.subAssetList.length > 0) {
        openAsset.subAssetList.forEach(item => {
          const mappedType = assetTypeMapping[item.value] || item.value;
          if (mappedType && !subAssetTypes.includes(mappedType)) {
            subAssetTypes.push(mappedType);
          }
        });
      }

      // Also try getting from subAssetType (normal Building Profile)
      if (openAsset?.subAssetType) {
        const mappedType = assetTypeMapping[openAsset.subAssetType] || openAsset.subAssetType;
        if (!subAssetTypes.includes(mappedType)) {
          subAssetTypes.push(mappedType);
        }
      }

      // Fallback: extract sub-asset types from the data itself (handles initial render when openAsset.subAssetList is not yet populated)
      if (subAssetTypes.length === 0 && properties?.length > 0) {
        properties.forEach(prop => {
          prop.nodeValue?.forEach(node => {
            if (node.subAsset?.name) {
              const mappedType = assetTypeMapping[node.subAsset.name] || node.subAsset.name;
              if (!subAssetTypes.includes(mappedType)) {
                subAssetTypes.push(mappedType);
              }
            }
          });
        });
      }

      // Collect ALL special fields from ALL sub-asset types
      const allSpecialFields = [];
      subAssetTypes.forEach(subAssetType => {
        if (specialFieldsByType[subAssetType]) {
          allSpecialFields.push(...specialFieldsByType[subAssetType]);
        }
      });

      const filtered = properties.filter(property => {
        // Always hide SHORT NAME
        const isShortName = property.nodeName?.toUpperCase().includes('SHORT NAME') ||
                            property.nodeName?.toUpperCase().includes('KURZBEZEICHNUNG') ||
                            property.nodeName?.toUpperCase().includes('KURZE BEZEICHNUNG') ||
                            property.labelTranslation?.some(label =>
                              label.title?.toUpperCase().includes('SHORT NAME') ||
                              label.title?.toUpperCase().includes('KURZBEZEICHNUNG') ||
                              label.title?.toUpperCase().includes('KURZE BEZEICHNUNG')
                            );
        if (isShortName) {
          return false;
        }

        // Check if it's a common field (show for all)
        if (propertyMatchesKeywords(property, commonFields)) {
          return true;
        }

        // Check if it's a special field for ANY of the asset types
        if (allSpecialFields.length > 0 && propertyMatchesKeywords(property, allSpecialFields)) {
          return true;
        }

        // Hide everything else
        return false;
      });
      setPropertyList(filtered);
    }, [properties, openAsset?.subAssetType, JSON.stringify(openAsset?.subAssetList), openAsset?.ident]);

    const updatePropertyList = (item, nodeValues) => {
      let temp = [...propertyList];
      const index = temp.findIndex((t) => t.nodeIdent === item.nodeIdent);
      if(index !== -1) {
        temp[index] = {...temp[index], nodeValue: nodeValues, isUpdated: true};
      }
      setPropertyList(temp);
      onListUpdate(temp);
    }

    const sortedItemList = useMemo(() => {
      // Use original order from database for all properties
      return propertyList.toSorted((a, b) => (a.nodeOrder || a.order || 999) - (b.nodeOrder || b.order || 999));
    }, [propertyList, openAsset?.subAssetType, properties]);

    const getPropertyName = (property) => {
        const inputObj = property?.labelTranslation.find(
          (e) => e.language === scope.projectLanguageCode
        );
        return getVariantText(inputObj.title || property?.name, "body1", {
          fontWeight: "bold",
          color: "#254a9a",
          margin: 5,
          textTransform: "uppercase",
        });
      };

  return (
    <>
       {sortedItemList.map((item, index) => {
              return (
                <tr
                  key={item.nodeIdent}
                  id={item.nodeIdent}
                >
                  <td
                    id={`text-${item.nodeIdent}`}
                    style={{
                      wordWrap: "break-word",
                      width: "25%",
                      fontSize: "12px",
                      verticalAlign: "baseline",
                    }}
                  >
                    <div className="sticky-text p-1"
                    >
                    {getPropertyName(item)}
                    </div>
                  </td>
                  <td style={{ width: "75%" }}>
                    <List className={"w-100 p-0"}>
                      <RenderNodeValues item={item} 
                      onUpdate={(item, nodeValue) =>{
                        updatePropertyList(item, nodeValue)
                      }}/>
                    </List>
                  </td>
                </tr>
              );
            })}
            </>
  )
}

export default RenderProperty